import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plane, ShoppingCart, Settings, Wrench, CheckCircle } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { services, stats, testimonials } from '../data/mock';
import { Reveal, EASE } from '../components/Reveal';

const heroItem = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } }
};

const Home = () => {
  const iconMap = {
    Plane: Plane,
    ShoppingCart: ShoppingCart,
    Settings: Settings,
    Wrench: Wrench
  };

  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '55%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    // React sets `muted` as a property after mount — set it explicitly before play()
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video with Overlay (parallax) */}
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <video
            ref={videoRef}
            className="w-full h-full object-cover scale-110"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="https://images.unsplash.com/photo-1619659085985-f51a00f0160a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxwcml2YXRlJTIwamV0fGVufDB8fHx8MTc3MTA0NTY2Mnww&ixlib=rb-4.1.0&q=85"
            data-testid="hero-video"
          >
            <source src="/hero-video.webm" type="video/webm" />
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/85 to-slate-800/80"></div>
        </motion.div>

        {/* Content */}
        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.16, delayChildren: 0.2 } } }}
        >
          <motion.h1 variants={heroItem} className="text-5xl md:text-7xl font-bold text-chrome mb-6">
            Beyond Skies
          </motion.h1>
          <motion.p variants={heroItem} className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto">
            One-roof solutions for Aircraft Leasing, Spares & Parts, Services & MRO in the Aviation Sector
          </motion.p>
          <motion.div variants={heroItem} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/services"
              className="group px-8 py-4 btn-chrome rounded-lg font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:scale-105 flex items-center space-x-2"
            >
              <span>Explore Services</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-transparent border-2 border-slate-300 text-white rounded-lg font-semibold text-lg hover:bg-white/10 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get in Touch
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-slate-400 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Reveal key={index} delay={index * 0.08} className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-chrome mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm md:text-base">{stat.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Comprehensive aviation solutions under one roof
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Reveal
                  key={service.id}
                  delay={index * 0.1}
                  className="group bg-slate-900 rounded-xl p-6 hover:bg-slate-800 transition-all duration-300 border border-slate-800 hover:border-slate-700 hover:shadow-2xl hover:shadow-slate-700/20 hover:-translate-y-2"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-slate-300" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="text-slate-300 hover:text-white text-sm font-medium flex items-center space-x-2 group/link"
                  >
                    <span>Learn more</span>
                    <ArrowRight className="group-hover/link:translate-x-1 transition-transform duration-200" size={16} />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal x={-40} y={0}>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Why Choose ONEAVIATION?
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                With over 15 years of excellence in the aviation industry, we deliver unmatched expertise and reliability.
              </p>
              <div className="space-y-4">
                {[
                  'Industry-leading expertise and experience',
                  '24/7 customer support and AOG assistance',
                  'Comprehensive one-stop aviation solutions',
                  'Global network and partnerships',
                  'Certified and compliant operations',
                  'Competitive pricing and flexible terms'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-slate-400 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal x={40} y={0} className="relative">
              <img
                src="https://images.unsplash.com/photo-1585916788784-64aa099fec1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhaXJjcmFmdCUyMGhhbmdhcnxlbnwwfHx8MTc3MTA0NTY2Njd8MA&ixlib=rb-4.1.0&q=85"
                alt="Aviation Facility"
                className="rounded-2xl shadow-2xl"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Client Testimonials</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Hear what our clients say about us
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Reveal
                key={testimonial.id}
                delay={index * 0.12}
                className="bg-slate-900 rounded-xl p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20"
              >
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.name}</h4>
                    <p className="text-slate-400 text-sm">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  "{testimonial.content}"
                </p>
                <div className="flex space-x-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-chrome inline-block text-lg">★</span>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-chrome mb-6">
            Ready to Take Flight?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Get in touch with our team today for customized aviation solutions
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 btn-chrome rounded-lg font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:scale-105"
          >
            <span>Contact Us Now</span>
            <ArrowRight size={20} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
